import { Fragment } from 'react'

import { cn } from '@/lib/cn'

/**
 * LessonContent merender isi materi pelajaran.
 *
 * Isi materi disimpan sebagai teks biasa, bukan HTML — teks yang datang dari
 * basis data dan dirender sebagai HTML adalah jalan paling langsung menuju
 * XSS tersimpan, dan pengajar memang tidak membutuhkan HTML untuk mengajar.
 * Yang dikenali di sini hanya empat penanda: judul bagian, daftar berpoin,
 * daftar bernomor, dan blok kode. Selebihnya diperlakukan sebagai paragraf.
 *
 * Blok kode penting: kelas pemrograman dan SQL tidak dapat diajarkan dengan
 * huruf proporsional yang membuang spasi rangkap.
 */
export function LessonContent({ content, className }: { content: string; className?: string }) {
  const blok = uraikan(content)

  if (blok.length === 0) return null

  return (
    <div className={cn('space-y-4 leading-relaxed text-muted', className)}>
      {blok.map((b, index) => {
        switch (b.jenis) {
          case 'judul':
            return (
              <h3 key={index} className="pt-2 text-base font-extrabold tracking-tight text-ink">
                {b.teks}
              </h3>
            )

          case 'kode':
            return (
              <pre
                key={index}
                className="overflow-x-auto rounded-xl border border-line bg-sunken p-4 text-[13px] leading-relaxed"
              >
                <code className="font-mono text-ink">{b.teks}</code>
              </pre>
            )

          case 'poin':
            return (
              <ul key={index} className="space-y-1.5">
                {b.butir.map((butir, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-brand" />
                    <span>{butir}</span>
                  </li>
                ))}
              </ul>
            )

          case 'nomor':
            return (
              <ol key={index} className="space-y-1.5">
                {b.butir.map((butir, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="mono-label shrink-0 text-[11px] text-brand tabular-nums">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span>{butir}</span>
                  </li>
                ))}
              </ol>
            )

          default:
            return (
              <p key={index}>
                {/* Baris tunggal di dalam satu paragraf tetap dihormati:
                    pengajar memakainya untuk memisahkan kalimat yang memang
                    ingin berdiri sendiri. */}
                {b.teks.split('\n').map((baris, i, semua) => (
                  <Fragment key={i}>
                    {baris}
                    {i < semua.length - 1 ? <br /> : null}
                  </Fragment>
                ))}
              </p>
            )
        }
      })}
    </div>
  )
}

type Blok =
  | { jenis: 'paragraf' | 'judul' | 'kode'; teks: string }
  | { jenis: 'poin' | 'nomor'; butir: string[] }

/** uraikan memecah teks materi menjadi blok-blok yang dapat dirender. */
function uraikan(content: string): Blok[] {
  const baris = content.replace(/\r\n/g, '\n').split('\n')
  const blok: Blok[] = []

  let paragraf: string[] = []
  let butir: string[] = []
  let jenisDaftar: 'poin' | 'nomor' | null = null
  let kode: string[] | null = null

  const tutupParagraf = () => {
    if (paragraf.length) blok.push({ jenis: 'paragraf', teks: paragraf.join('\n') })
    paragraf = []
  }
  const tutupDaftar = () => {
    if (butir.length && jenisDaftar) blok.push({ jenis: jenisDaftar, butir })
    butir = []
    jenisDaftar = null
  }

  for (const b of baris) {
    // Blok kode ditangani lebih dulu: di dalamnya, semua penanda lain
    // adalah kode biasa, bukan perintah format.
    if (b.trimStart().startsWith('```')) {
      if (kode === null) {
        tutupParagraf()
        tutupDaftar()
        kode = []
      } else {
        blok.push({ jenis: 'kode', teks: kode.join('\n') })
        kode = null
      }
      continue
    }
    if (kode !== null) {
      kode.push(b)
      continue
    }

    if (b.trim() === '') {
      tutupParagraf()
      tutupDaftar()
      continue
    }

    if (b.startsWith('## ')) {
      tutupParagraf()
      tutupDaftar()
      blok.push({ jenis: 'judul', teks: b.slice(3).trim() })
      continue
    }

    const nomor = b.match(/^(\d+)\.\s+(.*)$/)
    if (nomor) {
      tutupParagraf()
      if (jenisDaftar !== 'nomor') tutupDaftar()
      jenisDaftar = 'nomor'
      butir.push(nomor[2] ?? '')
      continue
    }

    if (b.startsWith('- ')) {
      tutupParagraf()
      if (jenisDaftar !== 'poin') tutupDaftar()
      jenisDaftar = 'poin'
      butir.push(b.slice(2).trim())
      continue
    }

    tutupDaftar()
    paragraf.push(b)
  }

  // Blok kode yang tidak pernah ditutup tetap ditampilkan apa adanya, bukan
  // dibuang: isi materi yang hilang jauh lebih merugikan daripada satu blok
  // yang tampilannya kurang rapi.
  if (kode !== null && kode.length) blok.push({ jenis: 'kode', teks: kode.join('\n') })
  tutupParagraf()
  tutupDaftar()

  return blok
}
